import React, { useContext, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  ProjectionKind,
  useCardPaymentsDataTable
} from 'generated/resources/datatable.graphql'
import {
  BankingCardPaymentFilter,
  BankingEntitiesCardPaymentCalculatedLabelEnum,
  CardDetailFragment
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { AccountActionsContext } from 'modules/banking/contexts/AccountActionsContext'
import { statusText } from 'modules/banking/lib/cardPayments/statusText'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

import { AccountCardPaymentsInsights } from 'modules/banking/components/AccountCardPaymentsInsights'
import { CardPaymentStatusTag } from './_CardPaymentStatusTag'

export const CardPaymentsDataTable: React.FC<{
  accountId?: number
  card?: CardDetailFragment
  tableIdentifier?: string
  showInsights?: boolean
}> = ({ accountId, card, tableIdentifier = 'cardPaymentsTable', showInsights }) => {
  const {
    access: { locale }
  } = useCurrentSession()
  const [searchValue, setSearchValue] = useState('')
  const { currency: accountCurrency } = useContext(AccountActionsContext)
  const calculatedLabelOptions: BankingEntitiesCardPaymentCalculatedLabelEnum[] = [
    'complete',
    'decline',
    'refund',
    'reverse',
    'withhold'
  ]

  const source = useCardPaymentsDataTable(
    [
      {
        identifier: 'merchantName',
        title: i18n.t('banking.attributes.merchant'),
        kind: ProjectionKind.Custom,
        template: '{{ merchantName }}',
        render: ({ merchantName }) => {
          return <Text.Normal size="200">{merchantName}</Text.Normal>
        },
        sortBy: 'merchantName'
      },
      {
        identifier: 'amount',
        title: i18n.t('banking.attributes.amount'),
        kind: ProjectionKind.Custom,
        template: '{{ amountCents }} {{ currency }}',
        render: ({ amountCents, currency }) => {
          return (
            <Text.Normal size="200">
              {formattedAmount({
                amountCents: amountCents,
                currency,
                locale
              })}
            </Text.Normal>
          )
        },
        sortBy: 'amountCents'
      },
      {
        identifier: 'externalCreatedAt',
        title: i18n.t('banking.attributes.date'),
        kind: ProjectionKind.Custom,
        template: '{{ externalCreatedAt }}',
        render: ({ externalCreatedAt }) => {
          return (
            <Text.Normal size="200">
              {dateBackendDateTimeToUI(String(externalCreatedAt))}
            </Text.Normal>
          )
        },
        sortBy: 'externalCreatedAt'
      },
      {
        identifier: 'status',
        title: i18n.t('banking.attributes.status'),
        kind: ProjectionKind.Custom,
        template: '{{ internalStatus }}',
        render: (cardPayment) => {
          return (
            <Box flexDirection="row" alignItems="center" gap="s8">
              <CardPaymentStatusTag cardPayment={cardPayment} />
            </Box>
          )
        }
      }
    ],
    {
      variables: {
        accountIds: accountId ? [accountId] : undefined,
        cardIds: card ? [card.id] : undefined,
        search: searchValue
      }
    }
  )

  const cardPaymentsTypeFilter: DataTableFilter<BankingCardPaymentFilter>[] = [
    {
      identifier: 'cardPaymentCalculatedLabelFilter',
      title: i18n.t('expenses_v2.cards.table.status'),
      path: 'calculatedLabel',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: calculatedLabelOptions.map((status) => ({
            label: statusText(status),
            value: status
          }))
        }
      ]
    },
    {
      identifier: 'cardPaymentExternalCreatedAtFilter',
      title: i18n.t('banking.attributes.booking_date'),
      path: 'externalCreatedAt',
      default: false,
      type: FilterType.Date,
      categories: [
        {
          kind: 'DateTimePicker',
          operation: 'between',
          value: undefined
        }
      ]
    }
  ]

  source.identifier = tableIdentifier

  return (
    <Box overflowY="hidden">
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 25
        }}
        filters={cardPaymentsTypeFilter}
        sort={{ path: 'externalCreatedAt', direction: 'desc' }}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableDefaultActions
            search={{
              value: searchValue,
              onChange: (ev) => setSearchValue(ev.target.value),
              placeholder: i18n.t('banking.card_payments.table.search_placeholder')
            }}
          />
          {showInsights && accountCurrency && (
            <AccountCardPaymentsInsights
              locale={locale}
              currency={accountCurrency}
              accountIds={[Number(accountId)]}
              search={searchValue}
            />
          )}
          <DataTable.Table />
        </Box>
      </DataTable.Provider>
    </Box>
  )
}
